import React, { useState, useEffect } from 'react';
import { post } from 'axios';
import PropTypes from 'prop-types';

import If from 'components/If';
import ModalContainer from 'components/ModalContainer';

import FilledButton from 'components/Buttons/FilledButton';
import Switch from 'components/FormGroup/Switch';
import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';

import { sortArrayInAlphabeticalOrder } from 'utils/objectManipulations';
import endpoints from 'services/endpoints';

import { getSubmitObject, getTableData } from '../../ManageListboxes/utils';

import * as S from './styles';

const {
  editListboxesValues,
  editProjectStatusAndCategoryListboxesValues,
} = endpoints;

const ManageListboxValue = ({
  type,
  data,
  isOpen,
  setIsOpen,
  userID,
  setToastVisibility,
  setToastInfos,
  setObserver,
}) => {
  const {
    id,
    id_area,
    inputValue,
    administrador,
    gestor_direto,
    gerente_projetos,
    responsavel_tecnico,
    coustCenters,
    listName,
    fieldValue,
    isFunction,
    isProject,
  } = data;

  const [inputFieldValue, setFieldInputValue] = useState(inputValue || '');
  const [isDirectManager, setIsDirectManager] = useState(gestor_direto);
  const [isAdmin, setIsAdmin] = useState(administrador);
  const [isProjectManager, setIsProjectManager] = useState(gerente_projetos);
  const [isTechnicalResponsive, setIsTechnicalResponsive] = useState(
    responsavel_tecnico,
  );

  const [coustCentersOptions, setCoustCenterOptions] = useState([]);
  const [selectedCoustCenters, setSelectedCoustCenters] = useState(
    coustCenters,
  );
  const [areaOptions, setAreaOptions] = useState([]);
  const [selectedArea, setSelectedArea] = useState(id_area);

  useEffect(() => {
    const getFunctionOptions = async () => {
      const coustCentersData = await getTableData('centro_de_custo');
      const areasData = await getTableData('area_empresa');

      const areasList = areasData.map(area => ({
        label: area.descricao,
        value: area.id,
      }));

      const counstCentersList = coustCentersData.map(coustCenter => ({
        label: coustCenter.descricao,
        value: coustCenter.id,
      }));

      setAreaOptions(sortArrayInAlphabeticalOrder(areasList, 'label'));
      setCoustCenterOptions(
        sortArrayInAlphabeticalOrder(counstCentersList, 'label'),
      );
    };

    if (isFunction) {
      getFunctionOptions();
    }
  }, [isFunction]);

  async function handleSubmit() {
    const submitObject = {
      id,
      descricao: inputFieldValue,
    };

    try {
      if (!isFunction && !isProject) {
        await post(
          editListboxesValues,
          getSubmitObject(fieldValue, userID, submitObject, false),
        );
      }

      if (isFunction) {
        const submitInfos = getSubmitObject(
          fieldValue,
          userID,
          submitObject,
          false,
          true,
        );

        const additionalInfos = {
          gestor_direto: isDirectManager ? 1 : 0,
          gerente_projetos: isProjectManager ? 1 : 0,
          responsavel_tecnico: isTechnicalResponsive ? 1 : 0,
          administrador: isAdmin ? 1 : 0,
          id_area: selectedArea,
          centros_de_custo: selectedCoustCenters.map(
            ({ value: coustCenterValue }) => coustCenterValue,
          ),
        };

        submitInfos.data = {
          ...submitInfos.data,
          ...additionalInfos,
        };

        await post(editListboxesValues, submitInfos);
      }

      if (isProject) {
        await post(
          editProjectStatusAndCategoryListboxesValues,
          getSubmitObject(fieldValue, userID, submitObject, false),
        );
      }

      setToastVisibility(true);
      setToastInfos({
        type: 'success',
        message: `${
          type === 'add' ? 'Adição' : 'Edição'
        } realizada com sucesso`,
      });

      setObserver(current => !current);
      setIsOpen(false);
    } catch {
      setToastVisibility(true);
      setToastInfos({
        type: 'error',
        message: 'Erro ao completar a requisição. Tente novamente',
      });
    }
  }

  return (
    <ModalContainer
      headText={`${
        type === 'add' ? 'Adição: ' : 'Edição: '
      } Lista de ${listName}`}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <InputField
        labelText="Valor do campo"
        value={inputFieldValue}
        name="input_value"
        handleChange={({ target }) => setFieldInputValue(target.value)}
        placeholderText="Defina o nome do campo"
      />

      <If condition={isFunction}>
        <SelectField
          hasLabelMargin
          labelText="Área correspondente da função"
          placeholderText="Selecione uma área..."
          name="area"
          options={areaOptions}
          value={areaOptions.find(area => area.value === selectedArea)}
          handleChange={({ value: areaValue }) => setSelectedArea(areaValue)}
        />

        <SelectField
          isMulti
          hasLabelMargin
          labelText="Centros de Custos da função"
          placeholderText="Selecione os centros de custos..."
          name="coust_centers"
          options={coustCentersOptions}
          value={selectedCoustCenters}
          handleChange={coust => setSelectedCoustCenters(coust)}
        />

        <Switch
          labelText="Administrador"
          isChecked={isAdmin}
          onChange={() => setIsAdmin(!isAdmin)}
        />

        <Switch
          labelText="Gestor Direto"
          isChecked={isDirectManager}
          onChange={() => setIsDirectManager(!isDirectManager)}
        />

        <Switch
          labelText="Gerente de Projetos"
          isChecked={isProjectManager}
          onChange={() => setIsProjectManager(!isProjectManager)}
        />

        <Switch
          labelText="Responsável Técnico"
          isChecked={isTechnicalResponsive}
          onChange={() => setIsTechnicalResponsive(!isTechnicalResponsive)}
        />
      </If>

      <S.ButtonsWrapper>
        <FilledButton
          text="Cancelar"
          buttonClass="cancel"
          handleClick={() => setIsOpen(false)}
        />
        <FilledButton
          text="Salvar"
          buttonClass="confirm"
          handleClick={handleSubmit}
        />
      </S.ButtonsWrapper>
    </ModalContainer>
  );
};

export default ManageListboxValue;

ManageListboxValue.defaultProps = {
  data: {
    id: null,
    value: null,
    administrador: false,
    gestor_direto: false,
    gerente_projetos: false,
    responsavel_tecnico: false,
    coustCenters: [],
  },
};

ManageListboxValue.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    id_area: PropTypes.number,
    inputValue: PropTypes.string,
    administrador: PropTypes.bool,
    gestor_direto: PropTypes.bool,
    gerente_projetos: PropTypes.bool,
    responsavel_tecnico: PropTypes.bool,
    coustCenters: PropTypes.arrayOf(PropTypes.number),
    listName: PropTypes.string.isRequired,
    fieldValue: PropTypes.string.isRequired,
    isFunction: PropTypes.bool.isRequired,
    isProject: PropTypes.bool.isRequired,
  }),
  isOpen: PropTypes.bool.isRequired,
  userID: PropTypes.number.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  setToastVisibility: PropTypes.func.isRequired,
  setToastInfos: PropTypes.func.isRequired,
  setObserver: PropTypes.func.isRequired,
};
